/* eslint-disable @typescript-eslint/naming-convention */
/* oxlint-disable @typescript-eslint/no-unsafe-return */
/* oxlint-disable @typescript-eslint/no-unsafe-assignment */
/* oxlint-disable @typescript-eslint/no-explicit-any */

import type { QueryClient } from '@tanstack/react-query';
import type {
	defineAddress,
	defineArticle,
	defineBreadcrumb,
	defineItemList,
	defineListItem,
	defineOrganization,
	definePerson,
	defineQuestion,
	defineWebPage,
	Thing,
} from '@unhead/schema-org';
import hoistStatics from 'hoist-non-react-statics';
import type { useSeoMeta } from 'unhead';

import type { I18n } from '@change-corgi/core/i18n';
import type { EmptyIntersection } from '@change-corgi/core/types';
import type { ExtendedLoadableComponent } from '@change-corgi/core-react/loadable';
import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PageTitle } from '#src/shared/pageHead';
import type { Session, SessionConfiguration } from '#src/shared/session';

import type { PrefetchedUserData } from './context';
import { usePrefetchedData, usePrefetchedSession, usePrefetchedUserData } from './hook';

type ParsedQs = { [key: string]: string | ParsedQs | Array<string | ParsedQs> | undefined };

export type PrefetchContext = Readonly<{
	utilityContext: UtilityContext;
	track: TrackingFn;
	l10n: Readonly<{ locale: string; countryCode: string }>;
	path: string;
	params: Readonly<Record<string, string | undefined>>;
	query: ParsedQs;
	queryClient: QueryClient;
	/**
	 * Whether the user-agent matches a bot
	 */
	bot: boolean;
	/**
	 * session only available if server-rendering with user data (=> no caching)
	 */
	session?: Session;
	sessionConfiguration: SessionConfiguration;
	/**
	 * comes from prefetchUserData()
	 */
	getUserData: () => Promise<PrefetchedUserData | undefined>;
}>;
export type PrefetchUserContext = Readonly<{
	utilityContext: UtilityContext;
	track: TrackingFn;
	l10n: Readonly<{ locale: string; countryCode: string }>;
	path: string;
	params: Readonly<Record<string, string | undefined>>;
	query: ParsedQs;
	/**
	 * Whether the user-agent matches a bot
	 */
	bot: boolean;
	session: Session;
	sessionConfiguration: SessionConfiguration;
}>;
export type PrefetchDependenciesContext = Readonly<{
	l10n: Readonly<{ locale: string; countryCode: string }>;
	path: string;
	params: Readonly<Record<string, string | undefined>>;
	query: ParsedQs;
	/**
	 * Whether the user-agent matches a bot
	 */
	bot: boolean;
	/**
	 * session only available if server-rendering with user data (=> no caching)
	 */
	session?: Session;
	sessionConfiguration: SessionConfiguration;
	/**
	 * comes from prefetchUserData()
	 */
	getUserData: () => Promise<PrefetchedUserData | undefined>;
}>;

type PageHeadContext<D = any, U = any> = {
	prefetchedData: D;
	prefetchedUserData: U;
};
export type PageMetadata = NonNullable<Parameters<typeof useSeoMeta>[1]>;

export type SchemaOrgFunctions = {
	defineAddress: typeof defineAddress;
	defineArticle: typeof defineArticle;
	defineBreadcrumb: typeof defineBreadcrumb;
	defineItemList: typeof defineItemList;
	defineListItem: typeof defineListItem;
	defineOrganization: typeof defineOrganization;
	definePerson: typeof definePerson;
	defineQuestion: typeof defineQuestion;
	defineWebPage: typeof defineWebPage;
};

/** @knip-ignore */
export type PrefetchDataFunction<D = any> = (context: PrefetchContext) => Promise<D>;
export type PrefetchDataFunctionVoid = (context: PrefetchContext) => Promise<void>;
/** @knip-ignore */
export type PrefetchUserDataFunction<D = any> = (context: PrefetchUserContext) => Promise<D>;
export type PrefetchUserDataFunctionVoid = (context: PrefetchUserContext) => Promise<void>;
/** @knip-ignore */
export type PageTitleFunction<D = any, U = any> = (
	context: PageHeadContext<D, U> & {
		i18n: I18n | UtilityContext['i18n'];
		/**
		 * Will generate the full title from a template title and a partial title
		 *
		 * @example
		 * fromTemplate('Hello Page') => 'Hello Page · Change.org'
		 */
		fromTemplate: (title: string) => PageTitle | string;
	},
) => string | PageTitle | undefined;
/** @knip-ignore */
export type PageMetadataFunction<D = any, U = any> = (
	context: PageHeadContext<D, U> & {
		i18n: I18n | UtilityContext['i18n'];
		getStaticAssetUrl: (url: string) => string;
		pageTitle: string | undefined;
		pageCanonical: string | undefined;
		params: Readonly<Record<string, string | undefined>>;
	},
) => PageMetadata | undefined;
/** @knip-ignore */
export type PageCanonicalFunction<D = any, U = any> = (
	context: PageHeadContext<D, U> & {
		params: Readonly<Record<string, string | undefined>>;
		pathname: string;
		query: ParsedQs;
	},
) => string | undefined;
/** @knip-ignore */
export type PageAlternateUrlsFunction<D = any, U = any> = (
	context: PageHeadContext<D, U>,
) => Record<string, string> | undefined;
export type PageImagePreloadLink = {
	href: string;
	media?: string;
	fetchPriority?: 'high' | 'low' | 'auto';
};
/** @knip-ignore */
export type PageImagePreloadLinksFunction<D = any, U = any> = (
	context: PageHeadContext<D, U>,
) => PageImagePreloadLink[] | undefined;
export type PageSchemaOrgItem = Thing;
/** @knip-ignore */
export type PageSchemaOrgFunction<D = any, U = any> = (
	context: PageHeadContext<D, U> & {
		i18n: I18n | UtilityContext['i18n'];
		getStaticAssetUrl: (pathname: string) => string;
		resolveUrl: (url: string) => string;
		pageTitle: string | undefined;
		pageCanonical: string | undefined;
	} & SchemaOrgFunctions,
) => PageSchemaOrgItem | readonly PageSchemaOrgItem[] | undefined;
/** @knip-ignore */
export type PrefetchedSessionProps = { prefetchedSession?: Session };
export type PrefetchedDataProps<D = any> = { prefetchedData?: D };
export type PrefetchedUserDataProps<D = any> = { prefetchedUserData?: D };
export type PrefetchDependency<PROPS = any> = PrefetchableComponentType<PROPS> &
	(EmptyIntersection | ExtendedLoadableComponent<PROPS>);
type StaticsBase = Readonly<{
	/**
	 * name to be used to distinguish fetched data from other components
	 */
	prefetchName: string;
	/**
	 * components for which prefetching must be done as well
	 */
	prefetchDependencies?: (
		context: PrefetchDependenciesContext,
	) => readonly PrefetchDependency[] | Promise<readonly PrefetchDependency[]>;
}>;
type WithPrefetchDataStatics<D = any> = Readonly<{
	prefetchData: PrefetchDataFunction<D>;
}>;
type WithoutPrefetchDataStatics = Readonly<{
	// this is to allow for conditional inference
	prefetchData?: PrefetchDataFunctionVoid;
}>;
type WithPrefetchUserDataStatics<D = any> = Readonly<{
	prefetchUserData: PrefetchUserDataFunction<D>;
}>;
type WithoutPrefetchUserDataStatics = Readonly<{
	// this is to allow for conditional inference
	prefetchUserData?: PrefetchUserDataFunctionVoid;
}>;
type WithPageHeadStatics<D = any, U = any> = Readonly<{
	/**
	 * title for the page to be rendered by the server
	 *
	 * NOTE: this only works if on the entry point to the page, not on prefetchDependencies
	 */
	pageTitle?: PageTitleFunction<D, U>;
	/**
	 * metadata (seo meta tags) for the page to be rendered by the server
	 *
	 * NOTE: this only works if on the entry point to the page, not on prefetchDependencies
	 */
	pageMetadata?: PageMetadataFunction<D, U>;
	/**
	 * canonical URL for the page to be rendered by the server
	 *
	 * if absent, the current path will be used
	 *
	 * NOTE: this only works if on the entry point to the page, not on prefetchDependencies
	 */
	pageCanonical?: PageCanonicalFunction<D, U>;
	/**
	 * alternate URLs for the page (hreflang => href mapping) to be rendered by the server
	 *
	 * NOTE: this only works if on the entry point to the page, not on prefetchDependencies
	 */
	pageAlternateUrls?: PageAlternateUrlsFunction<D, U>;
	/**
	 * schema.org for the page to be rendered by the server
	 *
	 * NOTE: this only works if on the entry point to the page, not on prefetchDependencies
	 */
	pageSchemaOrg?: PageSchemaOrgFunction<D, U>;
	/**
	 * image preload links for the page to be rendered by the server
	 *
	 * NOTE: this only works if on the entry point to the page, not on prefetchDependencies
	 */
	pageImagePreloadLinks?: PageImagePreloadLinksFunction<D, U>;
}>;

type OptionsBase = Readonly<{
	/**
	 * name to be used to distinguish fetched data from other components
	 */
	prefetchName: string;
	/**
	 * components for which prefetching must be done as well
	 */
	prefetchDependencies?:
		| readonly PrefetchDependency[]
		| ((
				context: PrefetchDependenciesContext,
		  ) => readonly PrefetchDependency[] | Promise<readonly PrefetchDependency[]>);
}>;

/**
 * Temporary hack to be able to get the right type inference for the prefetching functions
 * when no prefetching is needed
 *
 * @example
 * export const CookiePrefs = withPrefetchedData(
 * 	function CookiePrefs(_props: EmptyPrefetchProps) {
 * 		return (
 * 			<CookiePrefsPageContextProvider>
 * 				<Layout />
 * 			</CookiePrefsPageContextProvider>
 * 		);
 * 	},
 * 	{
 * 		prefetchName: __MODULE_HASH__,
 * 		pageTitle: ({ i18n }) => i18n.translate('fe.components.manage_cookies.title'),
 * 	},
 * );
 */
export type EmptyPrefetchProps = { ___do_not_use__?: boolean };

export function withPrefetchedData<
	PROPS,
	OPTS extends OptionsBase &
		(PROPS extends PrefetchedDataProps<infer DATA>
			? {
					prefetchData: PrefetchDataFunction<DATA>;
				}
			: { prefetchData?: PrefetchDataFunctionVoid }) &
		(PROPS extends PrefetchedUserDataProps<infer USER_DATA>
			? {
					prefetchUserData: PrefetchUserDataFunction<USER_DATA>;
				}
			: { prefetchUserData?: PrefetchUserDataFunctionVoid }) &
		(PROPS extends PrefetchedDataProps<infer DATA>
			? PROPS extends PrefetchedUserDataProps<infer USER_DATA>
				? WithPageHeadStatics<DATA, USER_DATA>
				: WithPageHeadStatics<DATA, never>
			: PROPS extends PrefetchedUserDataProps<infer USER_DATA>
				? WithPageHeadStatics<never, USER_DATA>
				: WithPageHeadStatics<never, never>),
>(
	component: React.ComponentType<PROPS>,
	options: OPTS,
): React.ComponentClass<
	Omit<
		PROPS,
		keyof PrefetchedDataProps | keyof PrefetchedUserDataProps | keyof PrefetchedSessionProps | keyof EmptyPrefetchProps
	>
> &
	StaticsBase &
	(OPTS extends { prefetchData: infer FN } ? { prefetchData: FN } : EmptyIntersection) &
	(OPTS extends { prefetchUserData: infer FN } ? { prefetchUserData: FN } : EmptyIntersection) {
	const displayName = `withPrefetchedData(${component.displayName || component.name})`;

	function WrappedComponent(props: any) {
		const Component = component as any;
		const prefetchedData = usePrefetchedData();
		const prefetchedUserData = usePrefetchedUserData();
		const prefetchedSession = usePrefetchedSession();

		return (
			<Component
				prefetchedData={prefetchedData?.[options.prefetchName]}
				prefetchedUserData={prefetchedUserData?.[options.prefetchName]}
				prefetchedSession={prefetchedSession}
				{...props}
			/>
		);
	}

	WrappedComponent.prefetchName = options.prefetchName;
	WrappedComponent.prefetchData = options.prefetchData && options.prefetchData.bind(options);
	WrappedComponent.prefetchUserData = options.prefetchUserData && options.prefetchUserData.bind(options);
	WrappedComponent.prefetchDependencies = Array.isArray(options.prefetchDependencies)
		? () => options.prefetchDependencies
		: options.prefetchDependencies;
	WrappedComponent.pageTitle = options.pageTitle && options.pageTitle.bind(options);
	WrappedComponent.pageMetadata = options.pageMetadata && options.pageMetadata.bind(options);
	WrappedComponent.pageCanonical = options.pageCanonical && options.pageCanonical.bind(options);
	WrappedComponent.pageAlternateUrls = options.pageAlternateUrls && options.pageAlternateUrls.bind(options);
	WrappedComponent.pageSchemaOrg = options.pageSchemaOrg && options.pageSchemaOrg.bind(options);
	WrappedComponent.pageImagePreloadLinks = options.pageImagePreloadLinks && options.pageImagePreloadLinks.bind(options);
	WrappedComponent.displayName = displayName;
	WrappedComponent.WrappedComponent = component;

	return hoistStatics(WrappedComponent as any, component);
}

export type PrefetchableComponentType<P = any> =
	| (React.ComponentType<P> &
			Readonly<{
				// this is to allow for conditional inference
				prefetchName?: undefined;
			}>)
	| (React.ComponentType<
			Omit<
				P,
				| keyof PrefetchedDataProps
				| keyof PrefetchedUserDataProps
				| keyof PrefetchedSessionProps
				| keyof EmptyPrefetchProps
			>
	  > &
			StaticsBase &
			(WithPrefetchDataStatics | WithoutPrefetchDataStatics) &
			(WithPrefetchUserDataStatics | WithoutPrefetchUserDataStatics) &
			WithPageHeadStatics);

function createPrefetchState<D extends Record<string, any>>(
	id: number | string,
	status: 'loaded',
	data: D,
): Readonly<D & { __prefetchId: number | string; status: 'loaded' }>;
function createPrefetchState<D extends Record<string, any> = EmptyIntersection>(
	id: number | string,
	status: 'error',
	data?: Record<string, any>,
): Readonly<D & { __prefetchId: number | string; status: 'error' }>;
function createPrefetchState(id: number | string, status: 'loaded' | 'error', data?: Record<string, any>): any {
	return { __prefetchId: id, status, ...data };
}

export { createPrefetchState };
